/** @jsxImportSource @emotion/react */
'use memo'
import css from '@emotion/css/macro'
import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { Spin } from 'antd'
import { LinkOutlined } from '@ant-design/icons'
import colors from '@sportninja/common/constants/appColors'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import { formatCurrency } from '@sportninja/common/utils/utils'
import { useSelector } from 'src/hooks/useRedux'
import { useUrlManager } from 'src/hooks/useUrlManager'
import RosterSelectNew, {
  useRosterSelect,
} from 'src/components/List/RosterSelectNew'
import SendInvoiceModal from 'src/components/SendInvoiceModal'
import Text from 'src/components/ts-components/Text'
import PayInFullModal from 'src/pages/Schedule/RegistrationDetailsModal/components/PayInFullModal'
import {
  getPayInFullEligibleRows,
  shouldShowPayInFullButton,
  sumSubtotal,
} from 'src/pages/Schedule/RegistrationDetailsModal/forms/RegistrationBillingForm/payInFullEligibility'
import { useBillingContext } from './context/BillingContext'
import { useUIContext } from './context/UIContext'
import BillingCards from './components/BillingCards'
import BillingTable from './components/BillingTable'

interface TeamBillingPageProps {
  teamId: string
  canEdit: boolean
}

const TeamBillingPage = ({ teamId, canEdit }: TeamBillingPageProps) => {
  const { setParam } = useUrlManager()
  const me = useSelector((state: any) => state.auth.user)
  const [isPayInFullModalOpen, setIsPayInFullModalOpen] = useState(false)

  const {
    loading: loadingRosters,
    rosters,
    selectedRosterId,
    setSelectedRosterId,
    loadRosters,
  } = useRosterSelect(teamId)

  const {
    loading,
    billingLoading,
    billingItems,
    currencyCode,
    error,
    fetchBilling,
  } = useBillingContext()

  const { sendInvoiceModalOpen, sendInvoiceBilling, closeSendInvoiceModal } =
    useUIContext()

  // Fetch billing when roster is selected
  useEffect(() => {
    if (selectedRosterId) {
      fetchBilling(selectedRosterId)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRosterId])

  const shouldDisplayBillingPayment = (() => {
    if (
      billingItems &&
      billingItems.length > 0 &&
      me?.id === billingItems[0]?.user_id
    ) {
      return true
    }
    if (canEdit) {
      return true
    }
    return false
  })()

  // "Pay Full Balance" entry point — mirrors the registration-side guard. Eligible rows are
  // the unpaid deposit/installment/full rows the consolidation would replace; every row for a
  // selected roster shares one team registration (hasOne), so the uid comes from the first
  // eligible row. The backend stays the authority on the actual charge.
  //
  // Gated on !loading && !billingLoading (same condition the table body hides behind): a roster
  // switch sets billingLoading before billingItems are replaced, so without this the button would
  // briefly render from the PREVIOUS roster's rows and open /pay for the wrong teamRegistrationId.
  const payInFullEligibleRows = getPayInFullEligibleRows(billingItems)
  const payInFullTeamRegistration = payInFullEligibleRows[0]?.team_registration
  const showPayInFull =
    !loading &&
    !billingLoading &&
    shouldDisplayBillingPayment &&
    shouldShowPayInFullButton(payInFullEligibleRows) &&
    Boolean(payInFullTeamRegistration?.id) &&
    !payInFullTeamRegistration?.is_comp &&
    !payInFullTeamRegistration?.is_cancelled

  const selectedRoster: any =
    rosters.find((r: any) => r.id === selectedRosterId) || rosters[0]

  const billingUserEmail = me?.email || ''
  const billingUserName = me?.name_full || me?.name || ''

  if (loadingRosters) {
    return (
      <div
        css={css`
          margin-top: 24px;
          display: flex;
          justify-content: center;
          align-items: center;
        `}
      >
        <Spin size='small' />
      </div>
    )
  }

  if (!me) {
    return (
      <div
        css={css`
          margin-top: 40px;
        `}
      >
        <p>We cannot show the financial details right now, try again later.</p>
      </div>
    )
  }

  if (rosters.length === 0) {
    return (
      <div
        css={css`
          margin-top: 40px;
        `}
      >
        <p>The team has no related charges.</p>
      </div>
    )
  }

  if (error && !loading && !billingLoading) {
    return (
      <div
        css={css`
          margin-top: 40px;
        `}
      >
        <p>We cannot show the financial details right now, try again later.</p>
      </div>
    )
  }

  return (
    <section
      css={css`
        height: 80vh;
        padding-bottom: 100px;
      `}
    >
      {/* Roster selector row */}
      <div
        css={css`
          display: flex;
          align-items: center;
          padding: 24px 0;
          gap: 20px;
        `}
      >
        <RosterSelectNew
          selectedRosterId={selectedRosterId}
          setSelectedRosterId={setSelectedRosterId}
          rosters={rosters}
          loadRosters={loadRosters}
          teamId={teamId}
          teamName=''
          permission={{ update: canEdit, admin: false }}
          setParam={setParam as any}
        />
        {rosters.length > 0 && selectedRoster && (
          <div
            css={css`
              display: flex;
              flex-direction: column;
              justify-content: space-between;
              height: 40px;
              gap: 8px;
              margin-bottom: 8px;
            `}
          >
            <Link
              to={`/schedule/${selectedRoster?.schedule_uid}`}
              css={css`
                display: flex;
                align-items: center;
                gap: 8px;
              `}
            >
              <p
                css={css`
                  font-size: 24px;
                  font-weight: 700;
                  font-family: ${isCanlan
                    ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                    : 'BarlowCondensed, Arial, Helvetica, sans-serif;'};
                  line-height: 32px;
                  overflow: hidden;
                  text-overflow: ellipsis;
                  white-space: nowrap;
                `}
              >
                {selectedRoster?.schedule_name}
              </p>
              <LinkOutlined
                css={css`
                  font-size: 24px;
                  color: ${colors.PRIMARY};
                `}
              />
            </Link>
            <p
              css={css`
                color: ${colors.NEUTRAL_50};
              `}
            >
              {selectedRoster?.division_name}
            </p>
          </div>
        )}
        {showPayInFull && (
          <button
            css={css`
              display: flex;
              margin-left: auto;
              /* The header row centers against the tall RosterSelect (label + 50px control),
                 which floats this 32px button too high. Bottom-anchor it and lift it so it
                 centers on the dropdown control: 8px (the select's own margin-bottom) +
                 (50 - 32) / 2 = 17px. Referencing the bottom keeps it correct regardless of
                 the label's height. */
              align-self: flex-end;
              margin-bottom: 17px;
              padding: 4px 8px;
              justify-content: center;
              align-items: center;
              gap: 4px;
              border-radius: 8px;
              border: 1px solid ${colors.PRIMARY};
              background: transparent;
              min-width: 62px;
              height: 32px;
              cursor: pointer;
            `}
            onClick={() => setIsPayInFullModalOpen(true)}
          >
            <Text
              variant='paragraphXSmall'
              color={colors.PRIMARY}
              weight='regular'
            >
              Pay Full Balance
            </Text>
          </button>
        )}
      </div>

      {/* Content */}
      {loading || billingLoading ? (
        <div
          css={css`
            display: flex;
            align-items: center;
            justify-content: center;
            margin-top: 24px;
          `}
        >
          <Spin size='small' />
        </div>
      ) : (
        <div
          css={css`
            display: flex;
            flex-direction: column;
            gap: 24px;
          `}
        >
          <BillingCards />
          <BillingTable
            shouldDisplayBillingPayment={shouldDisplayBillingPayment}
          />
        </div>
      )}

      <SendInvoiceModal
        isOpen={sendInvoiceModalOpen}
        onClose={closeSendInvoiceModal}
        defaultEmail={billingUserEmail}
        defaultName={billingUserName}
        billing={sendInvoiceBilling}
      />

      <PayInFullModal
        isOpen={isPayInFullModalOpen}
        onClose={() => setIsPayInFullModalOpen(false)}
        refetch={() => undefined}
        registrationId={payInFullTeamRegistration?.id || ''}
        email=''
        totalFormatted={formatCurrency(
          sumSubtotal(payInFullEligibleRows),
          currencyCode
        )}
        replacedCount={payInFullEligibleRows.length}
      />
    </section>
  )
}

export default TeamBillingPage
